import type { TemplateDataSettings } from "src/hooks/use-template-data-api/types";
import getPersonalCommits from "src/scenes/dashboard/mock-data/work-fundamentals/commits";
import getTeamCommits from "src/scenes/teams/dashboard/mock-data/work-fundamentals/commits";
import getPersonalDeployments from "src/scenes/dashboard/mock-data/work-fundamentals/deployments";
import getPersonalPipelines from "src/scenes/dashboard/mock-data/work-fundamentals/pipelines";
import getPersonalMergeRequests from "src/scenes/dashboard/mock-data/work-fundamentals/merge-requests";
import getPersonalMrTimings from "src/scenes/dashboard/mock-data/work-fundamentals/mr-timings";
import getPersonalReviews from "src/scenes/dashboard/mock-data/work-fundamentals/reviews";
import getPersonalSprintBreakdown from "src/scenes/dashboard/mock-data/work-fundamentals/sprint-breakdown";
import getPersonalCollaboration from "src/scenes/dashboard/mock-data/collaboration-fundamentals/collaboration";
import getPersonalWellbeing from "src/scenes/dashboard/mock-data/wellbeing-fundamentals/wellbeing";
import getTeamDeployments from "src/scenes/teams/dashboard/mock-data/work-fundamentals/deployments";
import getTeamMergeRequests from "src/scenes/teams/dashboard/mock-data/work-fundamentals/merge-pull";
import getTeamMrTimings from "src/scenes/teams/dashboard/mock-data/work-fundamentals/mr-timings";
import getTeamReviews from "src/scenes/teams/dashboard/mock-data/work-fundamentals/reviews";
import getTeamSprintBreakdown from "src/scenes/teams/dashboard/mock-data/work-fundamentals/sprint-breakdown";
import getTeamCollaboration from "src/scenes/teams/dashboard/mock-data/collaboration-fundamentals/collaboration";
import getTeamWellbeing from "src/scenes/teams/dashboard/mock-data/wellbeing-fundamentals/wellbeing";
import getPersonalWorkMiniIndexes from "src/scenes/dashboard/mock-data/work-fundamentals/mini-indexes";
import getPersonalCollaborationMiniIndexes from "src/scenes/dashboard/mock-data/collaboration-fundamentals/mini-indexes";
import getPersonalWellbeingMiniIndexes from "src/scenes/dashboard/mock-data/wellbeing-fundamentals/mini-indexes";
import getTeamWorkMiniIndexes from "src/scenes/teams/dashboard/mock-data/work-fundamentals/mini-indexes";
import getTeamCollaborationMiniIndexes from "src/scenes/teams/dashboard/mock-data/collaboration-fundamentals/mini-indexes";
import getTeamWellbeingMiniIndexes from "src/scenes/teams/dashboard/mock-data/wellbeing-fundamentals/mini-indexes";

const personalMapping = {
    "git.commits.changesPerLanguage": getPersonalCommits().changesPerLanguage,
    "git.commits.commitsInfo": getPersonalCommits().commitsInfo,
    "git.commits.commitsPerDay": getPersonalCommits(),
    "git.commits.totals": {
        ...getPersonalCommits().stats,
        baseline: getPersonalCommits().statsBaseline,
    },

    "git.deployments.deploymentsPerDay": {
        deploymentsPerDay: getPersonalDeployments().deploymentsPerDay,
        deploymentFrequencyRollbacks: getPersonalDeployments().deploymentFrequencyRollbacks,
    },
    "git.deployments.deploymentsInfo": getPersonalDeployments().deploymentsInfo,
    "git.deployments.deploymentStats": getPersonalDeployments().deploymentStats,

    "git.pipelines.pipelineFailures": getPersonalPipelines().pipelineFailures,
    "git.pipelines.highestRunningJobs": getPersonalPipelines().highestRunningJobs,
    "git.pipelines.slowestPipelines": getPersonalPipelines().slowestPipelines,
    "git.pipelines.durationPerStage": getPersonalPipelines().durationPerStage,
    "git.pipelines.pipelineStats": getPersonalPipelines().pipelineStats,

    "git.mrs.mrCollaborators": getPersonalMergeRequests().mrCollaborators,
    "git.mrs.mrsPerProject": getPersonalMergeRequests().mrsPerProject,
    "git.mrs.stuckMrs": getPersonalMergeRequests().stuckMrs,
    "git.mrs.stats": getPersonalMergeRequests().stats,
    "git.mrs.timings": getPersonalMrTimings().stats,

    "git.reviews.projectsCommentedOn": getPersonalReviews().projectsCommentedOn,
    "git.reviews.commentTimestamps": getPersonalReviews().commentTimestamps,
    "git.reviews.reviewStats": getPersonalReviews().reviewStats,

    "pm.tickets.workDistribution": getPersonalSprintBreakdown().workDistribution,
    "pm.tickets.ticketsAdded": getPersonalSprintBreakdown().ticketsAdded,
    "pm.tickets.ticketsOverview": getPersonalSprintBreakdown().ticketsOverview,
    "pm.tickets.stuckTickets": getPersonalSprintBreakdown().stuckTickets,
    "pm.tickets.ticketsAssigned": getPersonalSprintBreakdown().ticketsAssigned,
    "pm.tickets.bugs": getPersonalSprintBreakdown().bugs,
    "pm.tickets.timeSpentEstimated": getPersonalSprintBreakdown().timeSpentEstimated,
    "pm.tickets.ticketsBreakdown": getPersonalSprintBreakdown().ticketsBreakdown,
    "pm.tickets.timeStateDuration": getPersonalSprintBreakdown().timeStateDuration,
    "pm.tickets.totals": {
        ...getPersonalSprintBreakdown().stats,
        baseline: getPersonalSprintBreakdown().statsBaseline,
    },

    "collaboration.collaboration.timeCollaboratedPerDay":
        getPersonalCollaboration().timeCollaboratedPerDay,
    "collaboration.collaboration.commsPerDayHour": getPersonalCollaboration().commsPerDayHour,
    "collaboration.collaboration.channels": getPersonalCollaboration().channels,
    "collaboration.collaboration.messageCountPerDay": getPersonalCollaboration().messageCountPerDay,
    "collaboration.collaboration.meetingFrequency": getPersonalCollaboration().meetingFrequency,
    "collaboration.collaboration.meetingsBreakdownPerDay":
        getPersonalCollaboration().meetingsBreakdownPerDay,
    "collaboration.collaboration.meetingCollaborators":
        getPersonalCollaboration().meetingCollaborators,
    "collaboration.collaboration.meetingCancellations": {
        cancelled: getPersonalCollaboration().meetingsCancelled,
        rescheduled: getPersonalCollaboration().meetingsRescheduled,
    },
    "collaboration.collaboration.stats": {
        ...getPersonalCollaboration().stats,
        baseline: getPersonalCollaboration().statsBaseline,
    },

    "wellbeing.wellbeing.longOutHours": {
        longHours: getPersonalWellbeing().longHours,
        outOfHours: getPersonalWellbeing().outOfHours,
    },
    "wellbeing.wellbeing.upskilling": getPersonalWellbeing().upskilling,
    "wellbeing.wellbeing.fragmentation": getPersonalWellbeing().fragmentation,
    "wellbeing.wellbeing.focusTime": getPersonalWellbeing().focusTime,
    "wellbeing.wellbeing.support": getPersonalWellbeing().support,

    "indexes.timeseries.work_time": {
        timeseries: Object.values(
            getPersonalWorkMiniIndexes()
                .timeseries.filter((t) => t.subcategory === "work_time")
                .reduce((acc, curr) => {
                    if (!acc[curr.datetime]) {
                        acc[curr.datetime] = {
                            datetime: curr.datetime,
                        };
                    }
                    acc[curr.datetime][curr.name] = curr.value;
                    return acc;
                }, {})
        ),
    },
    "indexes.timeseries.work_speed": {
        timeseries: Object.values(
            getPersonalWorkMiniIndexes()
                .timeseries.filter((t) => t.subcategory === "work_speed")
                .reduce((acc, curr) => {
                    if (!acc[curr.datetime]) {
                        acc[curr.datetime] = {
                            datetime: curr.datetime,
                        };
                    }
                    acc[curr.datetime][curr.name] = curr.value;
                    return acc;
                }, {})
        ),
    },
    "indexes.timeseries.work_process": {
        timeseries: Object.values(
            getPersonalWorkMiniIndexes()
                .timeseries.filter((t) => t.subcategory === "work_process")
                .reduce((acc, curr) => {
                    if (!acc[curr.datetime]) {
                        acc[curr.datetime] = {
                            datetime: curr.datetime,
                        };
                    }
                    acc[curr.datetime][curr.name] = curr.value;
                    return acc;
                }, {})
        ),
    },

    "indexes.timeseries.collaboration_time": {
        timeseries: Object.values(
            getPersonalCollaborationMiniIndexes()
                .timeseries.filter((t) => t.subcategory === "collaboration_time")
                .reduce((acc, curr) => {
                    if (!acc[curr.datetime]) {
                        acc[curr.datetime] = {
                            datetime: curr.datetime,
                        };
                    }
                    acc[curr.datetime][curr.name] = curr.value;
                    return acc;
                }, {})
        ),
    },
    "indexes.timeseries.collaboration_network": {
        timeseries: Object.values(
            getPersonalCollaborationMiniIndexes()
                .timeseries.filter((t) => t.subcategory === "collaboration_network")
                .reduce((acc, curr) => {
                    if (!acc[curr.datetime]) {
                        acc[curr.datetime] = {
                            datetime: curr.datetime,
                        };
                    }
                    acc[curr.datetime][curr.name] = curr.value;
                    return acc;
                }, {})
        ),
    },

    "indexes.timeseries.wellbeing_balance": {
        timeseries: Object.values(
            getPersonalWellbeingMiniIndexes()
                .timeseries.filter((t) => t.subcategory === "wellbeing_balance")
                .reduce((acc, curr) => {
                    if (!acc[curr.datetime]) {
                        acc[curr.datetime] = {
                            datetime: curr.datetime,
                        };
                    }
                    acc[curr.datetime][curr.name] = curr.value;
                    return acc;
                }, {})
        ),
    },
    "indexes.timeseries.wellbeing_patterns": {
        timeseries: Object.values(
            getPersonalWellbeingMiniIndexes()
                .timeseries.filter((t) => t.subcategory === "wellbeing_patterns")
                .reduce((acc, curr) => {
                    if (!acc[curr.datetime]) {
                        acc[curr.datetime] = {
                            datetime: curr.datetime,
                        };
                    }
                    acc[curr.datetime][curr.name] = curr.value;
                    return acc;
                }, {})
        ),
    },

    "indexes.total.work_time": {
        current: getPersonalWorkMiniIndexes().metrics.find((m) => m.name === "work-time")?.value,
    },
    "indexes.total.work_speed": {
        current: getPersonalWorkMiniIndexes().metrics.find((m) => m.name === "work-speed")?.value,
    },
    "indexes.total.work_process": {
        current: getPersonalWorkMiniIndexes().metrics.find((m) => m.name === "work-process")?.value,
    },
    "indexes.total.collaboration_time": {
        current: getPersonalCollaborationMiniIndexes().metrics.find(
            (m) => m.name === "collaboration-time"
        )?.value,
    },
    "indexes.total.collaboration_network": {
        current: getPersonalCollaborationMiniIndexes().metrics.find(
            (m) => m.name === "collaboration-network"
        )?.value,
    },

    "indexes.total.wellbeing_balance": {
        current: getPersonalWellbeingMiniIndexes().metrics.find(
            (m) => m.name === "wellbeing-balance"
        )?.value,
    },
    "indexes.total.wellbeing_patterns": {
        current: getPersonalWellbeingMiniIndexes().metrics.find(
            (m) => m.name === "wellbeing-patterns"
        )?.value,
    },

    "indexes.total.overall_work": {
        current: getPersonalWorkMiniIndexes().overall.value,
    },
    "indexes.total.overall_collaboration": {
        current: getPersonalCollaborationMiniIndexes().overall.value,
    },
    "indexes.total.overall_wellbeing": {
        current: getPersonalWellbeingMiniIndexes().overall.value,
    },
};

const teamMapping = {
    "git.commits.changesPerLanguage": getTeamCommits().changesPerLanguage,
    "git.commits.commitsInfo": getTeamCommits().commitsInfo,
    "git.commits.commitsPerDay": getTeamCommits(),
    "git.commits.totals": {
        ...getTeamCommits().stats,
        baseline: getTeamCommits().statsBaseline,
    },

    "git.deployments.deploymentsPerDay": {
        deploymentsPerDay: getTeamDeployments().deploymentsPerDay,
        deploymentFrequencyRollbacks: getTeamDeployments().deploymentFrequencyRollbacks,
    },
    "git.deployments.deploymentsInfo": getTeamDeployments().deploymentsInfo,
    "git.deployments.deploymentStats": getTeamDeployments().deploymentStats,

    "git.pipelines.pipelineFailures": getPersonalPipelines().pipelineFailures,
    "git.pipelines.highestRunningJobs": getPersonalPipelines().highestRunningJobs,
    "git.pipelines.slowestPipelines": getPersonalPipelines().slowestPipelines,
    "git.pipelines.durationPerStage": getPersonalPipelines().durationPerStage,
    "git.pipelines.pipelineStats": getPersonalPipelines().pipelineStats,

    "git.mrs.mrCollaborators": getTeamMergeRequests().mrCollaborators,
    "git.mrs.mrsPerProject": getTeamMergeRequests().mrsPerProject,
    "git.mrs.stuckMrs": getTeamMergeRequests().stuckMrs,
    "git.mrs.stats": getTeamMergeRequests().stats,
    "git.mrs.timings": getTeamMrTimings().stats,

    "git.reviews.projectsCommentedOn": getTeamReviews().projectsCommentedOn,
    "git.reviews.commentTimestamps": getTeamReviews().commentTimestamps,
    "git.reviews.reviewStats": getTeamReviews().reviewStats,

    "pm.tickets.workDistribution": getTeamSprintBreakdown().workDistribution,
    "pm.tickets.ticketsAdded": getTeamSprintBreakdown().ticketsAdded,
    "pm.tickets.ticketsOverview": getTeamSprintBreakdown().ticketsOverview,
    "pm.tickets.stuckTickets": getTeamSprintBreakdown().stuckTickets,
    "pm.tickets.ticketsAssigned": getTeamSprintBreakdown().ticketsAssigned,
    "pm.tickets.bugs": getTeamSprintBreakdown().bugs,
    "pm.tickets.timeSpentEstimated": getTeamSprintBreakdown().timeSpentEstimated,
    "pm.tickets.ticketsBreakdown": getTeamSprintBreakdown().ticketsBreakdown,
    "pm.tickets.timeStateDuration": getTeamSprintBreakdown().timeStateDuration,
    "pm.tickets.totals": {
        ...getTeamSprintBreakdown().stats,
        baseline: getTeamSprintBreakdown().statsBaseline,
    },

    "collaboration.collaboration.timeCollaboratedPerDay":
        getTeamCollaboration().timeCollaboratedPerDay,
    "collaboration.collaboration.commsPerDayHour": getTeamCollaboration().commsPerDayHour,
    "collaboration.collaboration.channels": getTeamCollaboration().channels,
    "collaboration.collaboration.messageCountPerDay": getTeamCollaboration().messageCountPerDay,
    "collaboration.collaboration.meetingFrequency": getTeamCollaboration().meetingFrequency,
    "collaboration.collaboration.meetingsBreakdownPerDay":
        getTeamCollaboration().meetingsBreakdownPerDay,
    "collaboration.collaboration.eventTypeBreakdownPerDay":
        getTeamCollaboration().eventTypeBreakdownPerDay,
    "collaboration.collaboration.meetingCollaborators": getTeamCollaboration().meetingCollaborators,
    "collaboration.collaboration.meetingCancellations": {
        cancelled: getTeamCollaboration().meetingsCancelled,
        rescheduled: getTeamCollaboration().meetingsRescheduled,
    },
    "collaboration.collaboration.stats": {
        ...getTeamCollaboration().stats,
        baseline: getTeamCollaboration().statsBaseline,
    },

    "wellbeing.wellbeing.longOutHours": {
        longHours: getTeamWellbeing().longHours,
        outOfHours: getTeamWellbeing().outOfHours,
    },
    "wellbeing.wellbeing.upskilling": getTeamWellbeing().upskilling,
    "wellbeing.wellbeing.fragmentation": getTeamWellbeing().fragmentation,
    "wellbeing.wellbeing.focusTime": getTeamWellbeing().focusTime,
    "wellbeing.wellbeing.support": getTeamWellbeing().support,

    "indexes.timeseries.work_time": {
        timeseries: Object.values(
            getPersonalWorkMiniIndexes()
                .timeseries.filter((t) => t.subcategory === "work_time")
                .reduce((acc, curr) => {
                    if (!acc[curr.datetime]) {
                        acc[curr.datetime] = {
                            datetime: curr.datetime,
                        };
                    }
                    acc[curr.datetime][curr.name] = curr.value;
                    return acc;
                }, {})
        ),
    },
    "indexes.timeseries.work_speed": {
        timeseries: Object.values(
            getPersonalWorkMiniIndexes()
                .timeseries.filter((t) => t.subcategory === "work_speed")
                .reduce((acc, curr) => {
                    if (!acc[curr.datetime]) {
                        acc[curr.datetime] = {
                            datetime: curr.datetime,
                        };
                    }
                    acc[curr.datetime][curr.name] = curr.value;
                    return acc;
                }, {})
        ),
    },
    "indexes.timeseries.work_process": {
        timeseries: Object.values(
            getPersonalWorkMiniIndexes()
                .timeseries.filter((t) => t.subcategory === "work_process")
                .reduce((acc, curr) => {
                    if (!acc[curr.datetime]) {
                        acc[curr.datetime] = {
                            datetime: curr.datetime,
                        };
                    }
                    acc[curr.datetime][curr.name] = curr.value;
                    return acc;
                }, {})
        ),
    },

    "indexes.timeseries.collaboration_time": {
        timeseries: Object.values(
            getPersonalCollaborationMiniIndexes()
                .timeseries.filter((t) => t.subcategory === "collaboration_time")
                .reduce((acc, curr) => {
                    if (!acc[curr.datetime]) {
                        acc[curr.datetime] = {
                            datetime: curr.datetime,
                        };
                    }
                    acc[curr.datetime][curr.name] = curr.value;
                    return acc;
                }, {})
        ),
    },
    "indexes.timeseries.collaboration_network": {
        timeseries: Object.values(
            getPersonalCollaborationMiniIndexes()
                .timeseries.filter((t) => t.subcategory === "collaboration_network")
                .reduce((acc, curr) => {
                    if (!acc[curr.datetime]) {
                        acc[curr.datetime] = {
                            datetime: curr.datetime,
                        };
                    }
                    acc[curr.datetime][curr.name] = curr.value;
                    return acc;
                }, {})
        ),
    },

    "indexes.timeseries.wellbeing_balance": {
        timeseries: Object.values(
            getTeamWellbeingMiniIndexes()
                .timeseries.filter((t) => t.subcategory === "wellbeing_balance")
                .reduce((acc, curr) => {
                    if (!acc[curr.datetime]) {
                        acc[curr.datetime] = {
                            datetime: curr.datetime,
                        };
                    }
                    acc[curr.datetime][curr.name] = curr.value;
                    return acc;
                }, {})
        ),
    },
    "indexes.timeseries.wellbeing_patterns": {
        timeseries: Object.values(
            getTeamWellbeingMiniIndexes()
                .timeseries.filter((t) => t.subcategory === "wellbeing_patterns")
                .reduce((acc, curr) => {
                    if (!acc[curr.datetime]) {
                        acc[curr.datetime] = {
                            datetime: curr.datetime,
                        };
                    }
                    acc[curr.datetime][curr.name] = curr.value;
                    return acc;
                }, {})
        ),
    },

    "indexes.total.work_time": {
        current: getTeamWorkMiniIndexes().metrics.find((m) => m.name === "work-time")?.value,
    },
    "indexes.total.work_speed": {
        current: getTeamWorkMiniIndexes().metrics.find((m) => m.name === "work-speed")?.value,
    },
    "indexes.total.work_process": {
        current: getTeamWorkMiniIndexes().metrics.find((m) => m.name === "work-process")?.value,
    },

    "indexes.total.collaboration_time": {
        current: getTeamCollaborationMiniIndexes().metrics.find(
            (m) => m.name === "collaboration-time"
        )?.value,
    },
    "indexes.total.collaboration_network": {
        current: getTeamCollaborationMiniIndexes().metrics.find(
            (m) => m.name === "collaboration-network"
        )?.value,
    },

    "indexes.total.wellbeing_balance": {
        current: getTeamWellbeingMiniIndexes().metrics.find((m) => m.name === "wellbeing-balance")
            ?.value,
    },
    "indexes.total.wellbeing_patterns": {
        current: getTeamWellbeingMiniIndexes().metrics.find((m) => m.name === "wellbeing-patterns")
            ?.value,
    },

    "indexes.total.overall_work": {
        current: getTeamWorkMiniIndexes().overall.value,
    },
    "indexes.total.overall_collaboration": {
        current: getTeamCollaborationMiniIndexes().overall.value,
    },
    "indexes.total.overall_wellbeing": {
        current: getTeamWellbeingMiniIndexes().overall.value,
    },

    "executive.total.tasks": {
        lead_time_git: 1021038,
        review_to_merge_time_git: 536607.17,
        lead_time_git_adj: 854601,
        review_to_merge_time_git_adj: 127963,
        throughput_git: 6,
        throughput_git_per_user_avg: 6,
        throughput_git_per_user_median: 6,
        lead_time_git_median: 355112,
        lead_time_git_90th: 2707384,
        lead_time_git_95th: 2707385,
        review_to_merge_time_git_median: 128299,
        review_to_merge_time_git_90th: 1480951,
        review_to_merge_time_git_95th: 2094161,
        git_lead_time_slope: -0.5644,
        git_review_time_slope: -0.7897,
        git_throughput_slope: 0.0143,
        lead_time_pm: null,
        lead_time_pm_adj: null,
        throughput_pm: 0,
        throughput_pm_per_user_avg: null,
        throughput_pm_per_user_median: null,
        lead_time_pm_median: null,
        lead_time_pm_90th: null,
        lead_time_pm_95th: null,
        pm_lead_time_slope: null,
        pm_throughput_slope: null,
    },
    "executive.total.investment": {
        collaboration_duration: 201349,
        long_hours: 17419,
        out_of_working_hours: 53625,
        ooo_break_duration: 5400,
        ooo_duration: 5400,
        break_duration: 0,
        code_duration: 990251,
        focus_duration: 912026,
        per_day_collaboration_duration: 33558.166666666664,
        per_day_long_hours: 2903.1666666666665,
        per_day_out_of_working_hours: 8937.5,
        per_day_ooo_break_duration: 900,
        per_day_ooo_duration: 900,
        per_day_break_duration: 0,
        per_day_code_duration: 165041.83333333334,
        per_day_focus_duration: 152004.33333333334,
        per_user_collaboration_duration: 25433,
        per_user_long_hours: 2177,
        per_user_out_of_working_hours: 6702,
        per_user_ooo_break_duration: 675,
        per_user_ooo_duration: 675,
        per_user_break_duration: 0,
        per_user_code_duration: 147142,
        per_user_focus_duration: 137363,
        per_user_day_collaboration_duration: 4238.833333333333,
        per_user_day_long_hours: 362.8333333333333,
        per_user_day_out_of_working_hours: 1117,
        per_user_day_ooo_break_duration: 112.5,
        per_user_day_ooo_duration: 112.5,
        per_user_day_break_duration: 0,
        per_user_day_code_duration: 24523.666666666668,
        per_user_day_focus_duration: 22893.833333333332,
        colab_slope: -0.19041,
        long_hours_slope: -0.40951,
        out_of_working_hours_slope: -0.31916,
        ooo_slope: -0.5,
        code_slope: 0.03138,
        focus_slope: 0.05098,
    },
    "executive.total.backlog": {
        issues: 31,
        issue_points: null,
        prod_issues: 0,
        prod_issue_points: null,
        bug_issues: 19,
        bug_points: null,
        prod_bug_issues: 0,
        prod_bug_points: null,
        new_issues: 0,
        new_issue_points: null,
        new_bug_issues: 0,
        new_bug_points: null,
        prod_new_issues: 0,
        prod_new_issue_points: null,
        prod_new_bug_issues: 0,
        prod_new_bug_points: null,
        issues_slope: 0,
        bug_issues_slope: null,
    },
    "executive.total.scope-creep": {
        totals: {
            sprints: 1,
            prePlanned: 0,
            scopeCreep: 7,
            total: 7,
        },
        slope: null,
        sprints: [
            {
                sprintName: "CP Sprint 1",
                sprintId: "18",
                issuesPreplanned: 0,
                scopeCreep: 7,
                total: 7,
            },
        ],
    },
    "executive.total.rework": {
        all_commits: 56,
        all_total: 54444,
        rework_total: 16387,
        non_rework_total: 38057,
        rework_total_ratio: 0.3,
        slope: -0.40617,
    },
    "executive.total.bugs-detected": {
        total_issues: 49,
        total_issue_points: null,
        issues: 0,
        issue_points: null,
        total_prod_issues: 0,
        total_prod_issue_points: null,
        prod_issues: 0,
        prod_issue_points: null,
        slope: 0,
    },
    "executive.total.maintainability": {
        maintainability_influence: 0.25,
        details: null,
        slope: 0.13,
    },
};

const getTemplateMockData = (body: TemplateDataSettings) => {
    const mapping = body.teamId ? teamMapping : personalMapping;
    const response = body.queries.reduce((acc, query) => {
        let data = mapping[query.id];
        if (query.extractFromProperty && data[query.extractFromProperty]) {
            data = data[query.extractFromProperty];
        }
        return {
            ...acc,
            [query.assignToProperty]: data,
        };
    }, {});
    return response;
};

export default getTemplateMockData;
